import React, {
    ReactElement,
} from "react";

import {
    ChatSettingsArtificialIntelligence,
    ArtificialIntelligenceSettingsConcentType,
} from "../../../../../__generated__/api";
import { ProxyProperty } from "../../../../../components/dirty-tracker/FormTracker";
import AIAgentSelector from "../../../../../components/pickers/ai-agents/AIAgentSelector";
import SettingsContainer from "../../../../../components/theme/settings";
import Toggle from "../../../../../components/theme/toggle/Toggle";
import Avatar from "../../../../../components/avatar/Avatar";
import Alert from "../../../../../components/theme/alert/Alert";
import Time from "../../../../../components/time/Time";

const CONSENT_TEXT = {
    [ArtificialIntelligenceSettingsConcentType.Allowed]: "Opted In",
    [ArtificialIntelligenceSettingsConcentType.Denied]: "Opted Out",
};

export interface Props {
    data: ProxyProperty<ChatSettingsArtificialIntelligence>,
}

export default function ArtificialIntelligenceSettings({ data }: Props): ReactElement {
    const properties = data.useBuilder(
        builder => builder
            .property("agentIds", parent => parent?.agentIds as string[])
            .property("isEnabled", parent => parent?.isEnabled ?? true),
    );

    return (

        <SettingsContainer.Section
            title="AI Settings"
            helpText="These settings allow you to specify which AI agents should be used for this channel."
        >
            <SettingsContainer.Setting
                title="Enabled"
                description="Should AI agents be enabled for this channel."
                permissions={SettingsContainer.Setting.Permissions.Administrator}
            >
                <Toggle
                    checked={properties.isEnabled.value ?? false}
                    size="medium"
                    onChange={event => properties.isEnabled.setValue(event.target.checked)}
                />
                {data.value.consent && (
                    <Alert variant="primary" className="mt-2">
                        <strong>{CONSENT_TEXT[data.value.consent.consent]}</strong>
                        {" by "}
                        <Avatar isCircle variant="xxs" src={data.value.consent.user?.avatarUrl as string} />
                        {data.value.consent.user?.name}
                        {" on "}
                        <Time date={data.value.consent.date} variant="date-time" />
                    </Alert>
                )}
            </SettingsContainer.Setting>
            <SettingsContainer.Setting
                title="Agents"
                description="Which AI agents should be used for this channel."
                permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                isVisible={properties.isEnabled.value}
            >
                <AIAgentSelector
                    className={SettingsContainer.Setting.PickerClassName}
                    isMulti
                    onSelected={properties.agentIds.setValue}
                    value={properties.agentIds.value}
                />
            </SettingsContainer.Setting>

        </SettingsContainer.Section>

    );
}

ArtificialIntelligenceSettings.defaultProps = {
};
